import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchCmsGalleryAlbumBySlug,
  fetchCmsGalleryAlbums,
  normalizeCmsGalleryAlbumForLegacyView,
  normalizeCmsGalleryAlbumsForLegacyView,
} from "../../api/cmsClient";
import { getCmsLocaleFromLanguage, hasEnglishCmsVersion } from "../../api/cmsLocale";
import {
  cacheCmsGalleryAlbumDetail,
  cacheCmsGalleryAlbums,
  createGalleryFallbackNotice,
  fetchLocalGallery,
  getCachedCmsGalleryAlbumDetail,
  getCachedCmsGalleryAlbums,
  shouldSuppressGalleryFallbackNotice,
} from "../../api/galleryFallback";
import Loading from "../ui/Loading";
import SEO from "../../seo/SEO";
import { buildSectionSeoTitle } from "../../seo/seoText";
import GalleryFallbackNotice from "./GalleryFallbackNotice";
import {
  mergeGalleryAlbumWithLocalMediaFallback,
  mergeGalleryAlbumsWithLocalMediaFallbacks,
} from "../../api/legacyMediaMerge";

const CATEGORY_ORDER_BY_LOCALE = {
  me: ["Studenti", "Događaji", "Erasmus+", "Crveni krst"],
  en: ["Students", "Events", "Erasmus+", "Red Cross"],
};

const GALLERY_TEXT = {
  me: {
    all: "Vidi sve",
    filterLabel: "Filter galerije",
    openAlbum: "Otvori galeriju",
    gallery: "Galerija",
    photosTitle: "fotografija",
    clickToView: "Klikni za pregled",
    dialogLabel: "Pregled fotografija",
    close: "Zatvori",
    previous: "Prethodna slika",
    next: "Sledeća slika",
    noImage: "Nema dostupne slike.",
    loadingAlbum: "Učitavanje albuma...",
    shown: "Prikazano",
    of: "od",
    loadMore: "Učitaj još",
    loadError: "Greška prilikom učitavanja galerije.",
    albumError: "Nije moguće učitati sve fotografije iz ovog albuma.",
    photo: "Fotografija",
  },
  en: {
    all: "View all",
    filterLabel: "Gallery filter",
    openAlbum: "Open gallery",
    gallery: "Gallery",
    photosTitle: "photos",
    clickToView: "Click to view",
    dialogLabel: "Photo preview",
    close: "Close",
    previous: "Previous image",
    next: "Next image",
    noImage: "No image available.",
    loadingAlbum: "Loading album...",
    shown: "Shown",
    of: "of",
    loadMore: "Load more",
    loadError: "Error while loading the gallery.",
    albumError: "Unable to load all photos from this album.",
    photo: "Photo",
  },
};
const CMS_GALLERY_LIMIT = 60;

const INITIAL_VISIBLE = 12;
const LOAD_STEP = 12;

const GALLERY_QUERY_PARAM = "album";

function compactGalleryText(value = "", maxLength = 165) {
  const normalized = String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
}

function getAlbumSlugFromSearch(search = "") {
  return new URLSearchParams(search).get(GALLERY_QUERY_PARAM) || "";
}

function getGalleryAlbumPath(album) {
  if (!album?.slug) return "/galerija";
  return `/galerija?album=${encodeURIComponent(album.slug)}`;
}

function getGalleryAlbumTitle(album, labels) {
  return album?.seoTitle || album?.title || album?.description || labels.gallery;
}

function getGalleryAlbumDescription(album, labels) {
  if (!album) {
    return labels.gallery === "Gallery"
      ? "Photo gallery of the Faculty of Business Economics and Law Bar: events, students, Erasmus activities and academic life."
      : "Galerija Fakulteta za poslovnu ekonomiju i pravo Bar: događaji, studenti, Erasmus aktivnosti i akademski život.";
  }

  const fallback = labels.gallery === "Gallery"
    ? `${album.totalPhotos || 0} photos in this FPEP Bar gallery album.`
    : `${album.totalPhotos || 0} fotografija u ovom FPEP Bar galerijskom albumu.`;

  return compactGalleryText(album.seoDescription || album.description || album.title || fallback, 170);
}

function getGalleryAlbumImage(album) {
  const candidates = [
    ...(Array.isArray(album?.ogImageCandidates) ? album.ogImageCandidates : []),
    album?.ogImage,
    ...(Array.isArray(album?.imgCandidates) ? album.imgCandidates : []),
    album?.img,
  ].filter(Boolean);

  return normalizeCandidateList(candidates)[0] || "/assets/img/logo/logo.png";
}


function toAbsoluteGalleryUrl(value = "") {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://fpep.me${url.startsWith("/") ? url : `/${url}`}`;
}

function buildGalleryJsonLd(album, labels) {
  if (!album) return null;

  const images = getItemImages(album)
    .map((candidates) => normalizeCandidateList(candidates)[0])
    .filter(Boolean)
    .map(toAbsoluteGalleryUrl)
    .slice(0, 12);

  return {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: getGalleryAlbumTitle(album, labels),
    description: getGalleryAlbumDescription(album, labels),
    url: toAbsoluteGalleryUrl(getGalleryAlbumPath(album)),
    image: images,
  };
}

function gallerySrc(path) {
  if (!path) return "";
  const value = String(path).trim();

  if (/^https?:\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }

  const clean = value.replace(/^\/+/, "");
  return `${process.env.PUBLIC_URL}/${clean}`;
}


function normalizeCandidateList(value) {
  const rawCandidates = Array.isArray(value) ? value : [value];
  const seen = new Set();

  return rawCandidates
    .map(gallerySrc)
    .filter(Boolean)
    .filter((candidate) => {
      if (seen.has(candidate)) return false;
      seen.add(candidate);
      return true;
    });
}

function FallbackImage({ candidates, alt, ...props }) {
  const normalizedCandidates = normalizeCandidateList(candidates);
  const candidatesKey = normalizedCandidates.join("|");
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [candidatesKey]);

  if (!normalizedCandidates.length) return null;

  return (
    <img
      {...props}
      src={normalizedCandidates[candidateIndex]}
      alt={alt}
      onError={(event) => {
        const nextIndex = candidateIndex + 1;
        if (nextIndex < normalizedCandidates.length) {
          event.currentTarget.src = normalizedCandidates[nextIndex];
          setCandidateIndex(nextIndex);
          return;
        }

        if (typeof props.onError === "function") {
          props.onError(event);
        }
      }}
    />
  );
}

function getItemImages(item) {
  const imageCandidates = [
    item?.imgCandidates?.length ? item.imgCandidates : item?.img,
    ...(
      Array.isArray(item?.subImageCandidates) && item.subImageCandidates.length
        ? item.subImageCandidates
        : item?.subImages || []
    ),
  ]
    .map(normalizeCandidateList)
    .filter((candidates) => candidates.length);

  const seenPrimaryUrls = new Set();

  return imageCandidates.filter((candidates) => {
    const primaryUrl = candidates[0];
    if (!primaryUrl || seenPrimaryUrls.has(primaryUrl)) return false;
    seenPrimaryUrls.add(primaryUrl);
    return true;
  });
}

function GalleryFilters({ categories, activeCategory, onChange, labels }) {
  return (
    <div
      className="fpep-gallery-filters"
      role="tablist"
      aria-label={labels.filterLabel}
    >
      {categories.map((cat) => (
        <button
          key={cat}
          type="button"
          className={`fpep-gallery-filter-btn ${
            cat === activeCategory ? "active" : ""
          }`}
          onClick={() => onChange(cat)}
          role="tab"
          aria-selected={cat === activeCategory}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

function GalleryGrid({ items, onOpen, labels }) {
  return (
    <div className="fpep-gallery-grid">
      {items.map((item) => {
        const totalPhotos = Number(item.totalPhotos || 1 + (item.subImages?.length || 0));
        const imageCandidates = item.imgCandidates?.length ? item.imgCandidates : item.img;
        const imageSrc = normalizeCandidateList(imageCandidates)[0];

        return (
          <button
            key={`${item.source || "local"}-${item.id}`}
            type="button"
            className="fpep-gallery-card"
            onClick={() => onOpen(item, 0)}
            aria-label={`${labels.openAlbum}: ${item.description}`}
          >
            <span className="fpep-gallery-media">
              {imageSrc ? (
                <FallbackImage
                  candidates={imageCandidates}
                  alt={item.description}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="fpep-gallery-placeholder" aria-hidden="true">
                  {labels.gallery}
                </span>
              )}

              {totalPhotos > 1 && (
                <span
                  className="fpep-gallery-count"
                  title={`${totalPhotos} ${labels.photosTitle}`}
                >
                  +{totalPhotos - 1}
                </span>
              )}

              <span className="fpep-gallery-hover" aria-hidden="true">
                <span className="fpep-gallery-hover-text">
                  {item.description}
                </span>
                <span className="fpep-gallery-hover-cta">
                  {labels.clickToView}
                </span>
              </span>
            </span>

            <span className="fpep-gallery-caption">{item.description}</span>
          </button>
        );
      })}
    </div>
  );
}

function GalleryModal({
  open,
  images,
  index,
  title,
  loading,
  error,
  onClose,
  onPrev,
  onNext,
  labels,
}) {
  if (!open) return null;

  const currentImage = images[index];
  const hasMultipleImages = images.length > 1;

  return (
    <div
      className="fpep-gallery-modal"
      role="dialog"
      aria-modal="true"
      aria-label={labels.dialogLabel}
      onClick={onClose}
    >
      <div
        className="fpep-gallery-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="fpep-gallery-modal-close"
          onClick={onClose}
          aria-label={labels.close}
        >
          ×
        </button>

        {hasMultipleImages && (
          <button
            type="button"
            className="fpep-gallery-modal-nav fpep-gallery-modal-prev"
            onClick={onPrev}
            aria-label={labels.previous}
          >
            ‹
          </button>
        )}

        {currentImage ? (
          <FallbackImage
            candidates={currentImage}
            alt={title}
            className="fpep-gallery-modal-img"
            loading="eager"
            decoding="async"
          />
        ) : (
          <div className="fpep-gallery-modal-empty">{labels.noImage}</div>
        )}

        {hasMultipleImages && (
          <button
            type="button"
            className="fpep-gallery-modal-nav fpep-gallery-modal-next"
            onClick={onNext}
            aria-label={labels.next}
          >
            ›
          </button>
        )}

        {(loading || error) && (
          <div className={`fpep-gallery-modal-status ${error ? "error" : ""}`}>
            {error || labels.loadingAlbum}
          </div>
        )}

        <div className="fpep-gallery-modal-meta">
          <div className="fpep-gallery-modal-title">{title}</div>
          <div className="fpep-gallery-modal-counter">
            {Math.min(index + 1, images.length || 1)} / {images.length || 1}
          </div>
        </div>
      </div>
    </div>
  );
}

function Galerija() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const cmsLocale = getCmsLocaleFromLanguage(i18n.language);
  const labels = GALLERY_TEXT[cmsLocale] || GALLERY_TEXT.me;
  const defaultCategory = labels.all;
  const categoryOrder = CATEGORY_ORDER_BY_LOCALE[cmsLocale] || CATEGORY_ORDER_BY_LOCALE.me;

  const [items, setItems] = useState([]);
  const [localMediaFallbackItems, setLocalMediaFallbackItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState(defaultCategory);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fallbackNotice, setFallbackNotice] = useState(null);
  const [fallbackSource, setFallbackSource] = useState("cms");
  const [reloadToken, setReloadToken] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);
  const [modalTitle, setModalTitle] = useState("");
  const [modalAlbumSlug, setModalAlbumSlug] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalLocale, setModalLocale] = useState(cmsLocale);
  const [activeAlbum, setActiveAlbum] = useState(null);

  const selectedAlbumSlug = useMemo(() => getAlbumSlugFromSearch(location.search), [location.search]);

  const syncAlbumQuery = useCallback(
    (slug, { replace = false } = {}) => {
      const params = new URLSearchParams(location.search);

      if (slug) {
        params.set(GALLERY_QUERY_PARAM, slug);
      } else {
        params.delete(GALLERY_QUERY_PARAM);
      }

      const query = params.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ""}${location.hash || ""}`, { replace });
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  const loadGallery = useCallback(async ({ signal } = {}) => {
    setLoading(true);
    setError("");
    setFallbackNotice(null);
    setFallbackSource("cms");

    try {
      const [response, localItemsForMediaFallback] = await Promise.all([
        fetchCmsGalleryAlbums({
          page: 1,
          limit: CMS_GALLERY_LIMIT,
          locale: cmsLocale,
          signal,
        }),
        fetchLocalGallery().catch(() => []),
      ]);
      const cmsItems = mergeGalleryAlbumsWithLocalMediaFallbacks(
        normalizeCmsGalleryAlbumsForLegacyView(response.data || []),
        localItemsForMediaFallback,
      );

      setLocalMediaFallbackItems(localItemsForMediaFallback);

      if (cmsItems.length) {
        setItems(cmsItems);
        cacheCmsGalleryAlbums(cmsItems, cmsLocale);
        return;
      }

      const localItems = localItemsForMediaFallback.length ? localItemsForMediaFallback : await fetchLocalGallery();
      setItems(localItems);
      setFallbackSource("local");
      setFallbackNotice(createGalleryFallbackNotice({ source: "local", reason: "empty" }));
    } catch (cmsError) {
      const cachedItems = getCachedCmsGalleryAlbums(cmsLocale);

      if (cachedItems.length) {
        setItems(cachedItems);
        setFallbackSource("cache");
        setFallbackNotice(createGalleryFallbackNotice({ source: "cache" }));
        return;
      }

      try {
        const localItems = await fetchLocalGallery();
        setItems(localItems);
        setFallbackSource("local");
        setFallbackNotice(
          shouldSuppressGalleryFallbackNotice(cmsError)
            ? null
            : createGalleryFallbackNotice({ source: "local" }),
        );
      } catch (localError) {
        setItems([]);
        setError(localError?.message || labels.loadError);
      }
    } finally {
      setLoading(false);
    }
  }, [cmsLocale, labels.loadError]);

  useEffect(() => {
    const controller = new AbortController();
    setActiveCategory(defaultCategory);
    loadGallery({ signal: controller.signal });

    return () => controller.abort();
  }, [loadGallery, reloadToken, defaultCategory]);

  const categories = useMemo(() => {
    const available = new Set(items.map((x) => x.category).filter(Boolean));
    const ordered = categoryOrder.filter((c) => available.has(c));
    const extra = [...available]
      .filter((c) => !categoryOrder.includes(c))
      .sort((a, b) => a.localeCompare(b, "sr-Latn-ME"));

    return [defaultCategory, ...ordered, ...extra];
  }, [items, categoryOrder, defaultCategory]);

  const filteredItems = useMemo(() => {
    if (activeCategory === defaultCategory) return items;
    return items.filter((x) => x.category === activeCategory);
  }, [items, activeCategory, defaultCategory]);

  const visibleItems = useMemo(
    () => filteredItems.slice(0, visibleCount),
    [filteredItems, visibleCount],
  );

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [activeCategory]);

  const hydrateCmsAlbumDetail = useCallback(async (item) => {
    if (item.source !== "cms" || !item.slug) return;

    const localFallbackItems = localMediaFallbackItems.length
      ? localMediaFallbackItems
      : await fetchLocalGallery().catch(() => []);

    if (!localMediaFallbackItems.length && localFallbackItems.length) {
      setLocalMediaFallbackItems(localFallbackItems);
    }

    const cachedAlbum = getCachedCmsGalleryAlbumDetail(item.slug, cmsLocale);
    if (cachedAlbum) {
      const mergedCachedAlbum = mergeGalleryAlbumWithLocalMediaFallback(cachedAlbum, localFallbackItems);
      const images = getItemImages(mergedCachedAlbum);
      setActiveAlbum(mergedCachedAlbum);
      setModalTitle(mergedCachedAlbum.description || mergedCachedAlbum.title || labels.photo);
      if (images.length) {
        setModalImages(images);
        setModalIndex(0);
      }
      return;
    }

    setModalLoading(true);
    setModalError("");

    try {
      const response = await fetchCmsGalleryAlbumBySlug(item.slug, { locale: cmsLocale });
      const hydratedAlbum = mergeGalleryAlbumWithLocalMediaFallback(
        normalizeCmsGalleryAlbumForLegacyView(response.data),
        localFallbackItems,
      );

      if (!hydratedAlbum) return;

      cacheCmsGalleryAlbumDetail(item.slug, hydratedAlbum, cmsLocale);
      setActiveAlbum(hydratedAlbum);
      setModalTitle(hydratedAlbum.description || hydratedAlbum.title || labels.photo);
      setItems((currentItems) => {
        const hasExistingAlbum = currentItems.some(
          (currentItem) => currentItem.source === "cms" && currentItem.slug === item.slug,
        );
        const updatedItems = currentItems.map((currentItem) =>
          currentItem.source === "cms" && currentItem.slug === item.slug
            ? { ...currentItem, ...hydratedAlbum }
            : currentItem,
        );

        return hasExistingAlbum ? updatedItems : [hydratedAlbum, ...updatedItems];
      });

      setModalImages((currentImages) => {
        const nextImages = getItemImages(hydratedAlbum);
        return nextImages.length ? nextImages : currentImages;
      });
      setModalIndex(0);
    } catch (_error) {
      const localMergedAlbum = mergeGalleryAlbumWithLocalMediaFallback(item, localFallbackItems);
      const fallbackImages = getItemImages(localMergedAlbum);

      if (fallbackImages.length) {
        setActiveAlbum(localMergedAlbum);
        setModalImages(fallbackImages);
        setModalIndex(0);
        setModalError("");
      } else {
        setModalError(labels.albumError);
      }
    } finally {
      setModalLoading(false);
    }
  }, [localMediaFallbackItems, cmsLocale, labels.albumError, labels.photo]);

  const openModal = useCallback(
    (item, startIndex = 0, { syncUrl = true } = {}) => {
      const images = getItemImages(item);
      setActiveAlbum(item);
      setModalImages(images);
      setModalIndex(Math.min(startIndex, Math.max(images.length - 1, 0)));
      setModalTitle(item.description || item.title || labels.photo);
      setModalAlbumSlug(item.slug || "");
      setModalLocale(cmsLocale);
      setModalLoading(false);
      setModalError("");
      setModalOpen(true);

      if (syncUrl && item.slug) {
        syncAlbumQuery(item.slug);
      }

      hydrateCmsAlbumDetail(item);
    },
    [hydrateCmsAlbumDetail, labels.photo, syncAlbumQuery, cmsLocale],
  );

  const closeModal = useCallback(
    ({ syncUrl = true } = {}) => {
      setModalOpen(false);
      setModalAlbumSlug("");
      setModalLocale(cmsLocale);
      setModalLoading(false);
      setModalError("");
      setActiveAlbum(null);

      if (syncUrl) {
        syncAlbumQuery(null);
      }
    },
    [syncAlbumQuery, cmsLocale],
  );

  const nextImage = useCallback(() => {
    setModalIndex((i) => (i + 1) % modalImages.length);
  }, [modalImages.length]);

  const prevImage = useCallback(() => {
    setModalIndex((i) => (i - 1 + modalImages.length) % modalImages.length);
  }, [modalImages.length]);

  useEffect(() => {
    if (!modalOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (modalImages.length > 1 && e.key === "ArrowRight") nextImage();
      if (modalImages.length > 1 && e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") closeModal();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [modalOpen, modalImages.length, nextImage, prevImage, closeModal]);

  useEffect(() => {
    if (!modalOpen || !modalAlbumSlug) return;

    const album = items.find((item) => item.source === "cms" && item.slug === modalAlbumSlug);
    if (!album) return;

    setActiveAlbum(album);
    setModalTitle(album.description || album.title || labels.photo);

    const images = getItemImages(album);
    if (images.length > modalImages.length) {
      setModalImages(images);
      setModalIndex((currentIndex) => Math.min(currentIndex, images.length - 1));
    }
  }, [items, modalAlbumSlug, modalImages.length, modalOpen, labels.photo]);

  useEffect(() => {
    if (loading) return undefined;

    if (!selectedAlbumSlug) {
      if (modalOpen && modalAlbumSlug) {
        closeModal({ syncUrl: false });
      }
      return undefined;
    }

    if (modalOpen && modalAlbumSlug === selectedAlbumSlug && modalLocale === cmsLocale) {
      return undefined;
    }

    let cancelled = false;

    const openAlbumFromUrl = async () => {
      const existingAlbum = items.find(
        (item) => item.source === "cms" && item.slug === selectedAlbumSlug,
      );

      if (existingAlbum) {
        openModal(existingAlbum, 0, { syncUrl: false });
        return;
      }

      const localFallbackItems = localMediaFallbackItems.length
        ? localMediaFallbackItems
        : await fetchLocalGallery().catch(() => []);

      if (cancelled) return;

      const cachedAlbum = getCachedCmsGalleryAlbumDetail(selectedAlbumSlug, cmsLocale);
      if (cachedAlbum) {
        const mergedCachedAlbum = mergeGalleryAlbumWithLocalMediaFallback(cachedAlbum, localFallbackItems);
        openModal(mergedCachedAlbum, 0, { syncUrl: false });
        return;
      }

      setModalLoading(true);
      setModalError("");

      try {
        const response = await fetchCmsGalleryAlbumBySlug(selectedAlbumSlug, { locale: cmsLocale });
        const hydratedAlbum = mergeGalleryAlbumWithLocalMediaFallback(
          normalizeCmsGalleryAlbumForLegacyView(response.data),
          localFallbackItems,
        );

        if (!hydratedAlbum || cancelled) return;

        cacheCmsGalleryAlbumDetail(selectedAlbumSlug, hydratedAlbum, cmsLocale);
        setItems((currentItems) => {
          const hasExistingAlbum = currentItems.some(
            (item) => item.source === "cms" && item.slug === hydratedAlbum.slug,
          );

          return hasExistingAlbum ? currentItems : [hydratedAlbum, ...currentItems];
        });
        openModal(hydratedAlbum, 0, { syncUrl: false });
      } catch (_error) {
        if (!cancelled) {
          setModalError(labels.albumError);
        }
      } finally {
        if (!cancelled) {
          setModalLoading(false);
        }
      }
    };

    openAlbumFromUrl();

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    selectedAlbumSlug,
    items,
    modalOpen,
    modalAlbumSlug,
    modalLocale,
    openModal,
    closeModal,
    localMediaFallbackItems,
    cmsLocale,
    labels.albumError,
  ]);

  const onFilterChange = useCallback(
    (category) => setActiveCategory(category),
    [],
  );

  if (loading) return <Loading />;

  const seoAlbum = activeAlbum || (selectedAlbumSlug
    ? items.find((item) => item.source === "cms" && item.slug === selectedAlbumSlug)
    : null);
  const isMissingSelectedAlbum = Boolean(
    selectedAlbumSlug && !seoAlbum && !modalLoading && modalError,
  );
  const seoAlbumTitle = seoAlbum ? getGalleryAlbumTitle(seoAlbum, labels) : labels.gallery;
  const seoTitle = isMissingSelectedAlbum
    ? cmsLocale === "en"
      ? "Gallery album not found | FPEP Bar"
      : "Galerijski album nije pronađen | FPEP Bar"
    : buildSectionSeoTitle(
        seoAlbum ? seoAlbumTitle : labels.gallery,
        seoAlbum ? labels.gallery : "",
      );
  const seoDescription = isMissingSelectedAlbum
    ? cmsLocale === "en"
      ? "The requested FPEP Bar gallery album is unavailable or no longer published."
      : "Traženi galerijski album FPEP Bar nije dostupan ili više nije objavljen."
    : getGalleryAlbumDescription(seoAlbum, labels);
  const seoPath = seoAlbum
    ? getGalleryAlbumPath(seoAlbum)
    : selectedAlbumSlug
      ? `/galerija?album=${encodeURIComponent(selectedAlbumSlug)}`
      : "/galerija";
  const seoImage = seoAlbum
    ? getGalleryAlbumImage(seoAlbum)
    : "/assets/img/social/fpep-news-gallery.webp";
  const seoAlbumHasEnglishVersion = seoAlbum ? hasEnglishCmsVersion(seoAlbum) : true;
  const isEnglishFallbackAlbum = Boolean(
    seoAlbum && cmsLocale === "en" && !seoAlbumHasEnglishVersion,
  );
  const galleryJsonLd = isMissingSelectedAlbum ? null : buildGalleryJsonLd(seoAlbum, labels);

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        pathname={seoPath}
        pageType={seoAlbum ? "ImageGallery" : isMissingSelectedAlbum ? "WebPage" : "CollectionPage"}
        image={seoImage}
        imageAlt={seoAlbum ? seoAlbumTitle : seoTitle}
        imageType={!seoAlbum ? "image/webp" : undefined}
        imageWidth={!seoAlbum ? 1200 : undefined}
        imageHeight={!seoAlbum ? 630 : undefined}
        noindex={isEnglishFallbackAlbum || isMissingSelectedAlbum}
        canonicalLocale={isEnglishFallbackAlbum ? "me" : undefined}
        englishAlternateAvailable={!isMissingSelectedAlbum && seoAlbumHasEnglishVersion}
        disableLanguageAlternates={isMissingSelectedAlbum}
        breadcrumbs={[
          { name: cmsLocale === "en" ? "Home" : "Početna", path: "/" },
          { name: labels.gallery, path: "/galerija" },
          ...(seoAlbum ? [{ name: seoAlbumTitle, path: seoPath }] : []),
        ]}
        jsonLd={[galleryJsonLd]}
      />

      <section id="work" className="pt-100 pb-105 fpep-gallery">
        <div className="container">
          <div className="portfolio">
            <div className="row align-items-end">
              <div className="col-lg-12">
                <div className="text-center mb-35">
                  <GalleryFilters
                    categories={categories}
                    activeCategory={activeCategory}
                    onChange={onFilterChange}
                    labels={labels}
                  />

                  <div className="fpep-gallery-stats">
                    {labels.shown}{" "}
                    {Math.min(visibleItems.length, filteredItems.length)} {labels.of}{" "}
                    {filteredItems.length}
                  </div>

                  <GalleryFallbackNotice
                    message={fallbackNotice}
                    source={fallbackSource}
                    retrying={loading}
                    onRetry={() => setReloadToken((value) => value + 1)}
                  />

                  {error && <div className="fpep-gallery-error">{error}</div>}
                </div>
              </div>
            </div>

            <GalleryGrid items={visibleItems} onOpen={openModal} labels={labels} />

            {visibleCount < filteredItems.length && (
              <div className="fpep-gallery-loadmore-wrap">
                <button
                  type="button"
                  className="fpep-gallery-loadmore"
                  onClick={() => setVisibleCount((n) => n + LOAD_STEP)}
                >
                  {labels.loadMore}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <GalleryModal
        open={modalOpen && modalImages.length > 0}
        images={modalImages}
        index={modalIndex}
        title={modalTitle}
        loading={modalLoading}
        error={modalError}
        onClose={closeModal}
        onPrev={prevImage}
        onNext={nextImage}
        labels={labels}
      />
    </>
  );
}

export default Galerija;
